import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useNotifications } from "@/context/NotificationContext";
import {
  PenLine,
  FileText,
  Sparkles,
  LayoutGrid,
  FileSignature,
  Plus,
  Search,
  ArrowRight,
  MoreHorizontal,
  Download,
  Eye,
  Trash2,
  Loader2,
  Check,
  Wand2
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import SignatureCanvas from "react-signature-canvas";

import { API_URL } from "@/utils/api";

// Contract templates
const CONTRACT_TEMPLATES = [
  { id: "service", name: "Service Agreement", description: "Standard service contract for freelancers", content: `SERVICE AGREEMENT

This Service Agreement ("Agreement") is entered into as of [DATE] by and between:

CLIENT: [CLIENT_NAME]
PROVIDER: [YOUR_NAME]

SERVICES:
The Provider agrees to provide the following services:
[SERVICES_DESCRIPTION]

COMPENSATION:
The Client agrees to pay the Provider [AMOUNT] for the services described above.

TERM:
This Agreement shall commence on [START_DATE] and continue until [END_DATE] or until terminated by either party with 30 days written notice.

CONFIDENTIALITY:
Both parties agree to keep confidential any proprietary information shared during the course of this engagement.

SIGNATURES:
By signing below, both parties agree to the terms and conditions outlined in this Agreement.` },
  { id: "nda", name: "Non-Disclosure Agreement", description: "Protect confidential information", content: `NON-DISCLOSURE AGREEMENT

This Non-Disclosure Agreement ("Agreement") is made between:

DISCLOSING PARTY: [YOUR_NAME]
RECEIVING PARTY: [RECIPIENT_NAME]

PURPOSE:
The parties wish to explore a potential business relationship and may need to disclose confidential information.

CONFIDENTIAL INFORMATION:
"Confidential Information" means any non-public information disclosed by either party, including but not limited to business plans, financial data, customer lists, and technical information.

OBLIGATIONS:
The Receiving Party agrees to:
1. Keep all Confidential Information strictly confidential
2. Not disclose any Confidential Information to third parties
3. Use Confidential Information only for the stated purpose
4. Return or destroy all Confidential Information upon request

TERM:
This Agreement shall remain in effect for [DURATION] years from the date of signing.

SIGNATURES:` },
  { id: "consulting", name: "Consulting Agreement", description: "For consulting engagements", content: `CONSULTING AGREEMENT

This Consulting Agreement is entered into between:

CLIENT: [CLIENT_NAME]
CONSULTANT: [YOUR_NAME]

SCOPE OF WORK:
[WORK_DESCRIPTION]

FEES AND PAYMENT:
Consultant shall be compensated at a rate of [RATE] per hour/project.
Payment terms: Net 30 days from invoice date.

INDEPENDENT CONTRACTOR:
Consultant is an independent contractor and not an employee of the Client.

INTELLECTUAL PROPERTY:
All work product created under this Agreement shall be owned by [OWNER].

SIGNATURES:` },
  { id: "contractor", name: "Independent Contractor", description: "Hire contractors compliantly", content: `INDEPENDENT CONTRACTOR AGREEMENT

This Agreement is made between:

COMPANY: [COMPANY_NAME]
CONTRACTOR: [CONTRACTOR_NAME]

SERVICES:
Contractor agrees to provide the following services:
[SERVICES]

COMPENSATION:
[PAYMENT_TERMS]

RELATIONSHIP:
The Contractor is an independent contractor and not an employee. Contractor is responsible for all taxes and benefits.

TERM AND TERMINATION:
This Agreement may be terminated by either party with [NOTICE_PERIOD] written notice.

SIGNATURES:` },
];

export default function ContractsPage({ user }) {
  const signatureRef = useRef(null);
  const { notify } = useNotifications();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creating, setCreating] = useState(false);
  const [signing, setSigning] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);

  // Contract form
  const [contractForm, setContractForm] = useState({
    title: "",
    recipient_name: "",
    recipient_email: "",
    content: ""
  });

  // AI contract form
  const [aiForm, setAiForm] = useState({
    contract_type: "service",
    party_a_name: user?.company_name || user?.name || "",
    party_b_name: "",
    description: "",
    key_terms: "",
    duration: "",
    payment_terms: ""
  });

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await fetch(`${API_URL}/api/contracts`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setContracts(data);
      }
    } catch (err) {
      console.error("Error fetching contracts:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setContractForm(prev => ({
      ...prev,
      title: template.name,
      content: template.content
    }));
    setShowTemplateModal(false);
    setShowCreateModal(true);
  };

  const handleGenerateAIContract = async (e) => {
    e.preventDefault();
    setGeneratingAI(true);

    const token = localStorage.getItem("auth_token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    try {
      const res = await fetch(`${API_URL}/api/contracts/generate-ai`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(aiForm)
      });

      if (res.ok) {
        const result = await res.json();
        setContractForm(prev => ({
          ...prev,
          title: result.title,
          content: result.content,
          recipient_name: aiForm.party_b_name
        }));
        setShowAIModal(false);
        setShowCreateModal(true);
        toast.success("AI contract generated! Review and customize as needed.");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to generate contract");
      }
    } catch (err) {
      toast.error("Failed to generate AI contract");
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    setCreating(true);

    const token = localStorage.getItem("auth_token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    try {
      const res = await fetch(`${API_URL}/api/contracts`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          title: contractForm.title,
          recipient_name: contractForm.recipient_name,
          recipient_email: contractForm.recipient_email,
          content: contractForm.content,
          template_id: selectedTemplate?.id,
          status: "draft"
        })
      });

      if (res.ok) {
        notify.success("Contract Created", `"${contractForm.title}" has been created`, { category: "contract" });
        setShowCreateModal(false);
        resetForm();
        fetchContracts();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create contract");
      }
    } catch (err) {
      toast.error("Failed to create contract");
    } finally {
      setCreating(false);
    }
  };

  const handleSignContract = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error("Please provide a signature");
      return;
    }

    setSigning(true);
    const signatureData = signatureRef.current.toDataURL();
    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/contracts/${selectedContract.contract_id}/sign`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          signature_data: signatureData,
          signer_name: user?.name || "User"
        })
      });

      if (res.ok) {
        notify.contractSigned(selectedContract.title);
        setShowSignModal(false);
        fetchContracts();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to sign contract");
      }
    } catch (err) {
      toast.error("Failed to sign contract");
    } finally {
      setSigning(false);
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!confirm("Are you sure you want to delete this contract?")) return;

    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/contracts/${contractId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        toast.success("Contract deleted");
        fetchContracts();
      } else {
        toast.error("Failed to delete contract");
      }
    } catch (err) {
      toast.error("Failed to delete contract");
    }
  };

  const handleDownloadPDF = async (contractId) => {
    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/contracts/${contractId}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${contractId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("PDF downloaded successfully");
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (err) {
      toast.error("Error downloading PDF");
    }
  };

  const resetForm = () => {
    setContractForm({
      title: "",
      recipient_name: "",
      recipient_email: "",
      content: ""
    });
    setSelectedTemplate(null);
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  // Calculate stats
  const stats = {
    signature: contracts.filter(c => c.status === "pending_signature").length,
    sent: contracts.filter(c => c.status === "sent").length,
    draft: contracts.filter(c => c.status === "draft").length,
    completed: contracts.filter(c => c.status === "completed" || c.status === "signed").length,
  };

  const filteredContracts = contracts.filter(c => {
    if (activeTab === "sent") return c.status === "sent";
    if (activeTab === "signed") return c.status === "signed" || c.status === "completed";
    return true;
  }).filter(c =>
    c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.recipient_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <h1 className="text-2xl font-bold text-[#121212] font-['Manrope']">Contracts</h1>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-[#F7FFE5] border border-[#BFFF00]/30 rounded-full p-1">
                <TabsTrigger value="all" className="data-[state=active]:bg-[#121212] data-[state=active]:text-white rounded-full">All</TabsTrigger>
                <TabsTrigger value="sent" className="data-[state=active]:bg-[#121212] data-[state=active]:text-white rounded-full">Sent</TabsTrigger>
                <TabsTrigger value="signed" className="data-[state=active]:bg-[#121212] data-[state=active]:text-white rounded-full">Signed</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              onClick={() => setShowAIModal(true)}
              variant="outline"
              className="gap-2 flex-1 sm:flex-none rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"
              data-testid="ai-contract-btn"
            >
              <Wand2 className="w-4 h-4 text-[#121212]" />
              AI Generate
            </Button>
            <Button 
              onClick={() => setShowTemplateModal(true)}
              className="bg-[#121212] hover:bg-[#2a2a2a] gap-2 flex-1 sm:flex-none rounded-full px-6 font-semibold" 
              data-testid="create-contract-btn"
            >
              <Plus className="w-4 h-4" />
              New Contract
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-2 border-dashed border-[#BFFF00]/50 rounded-2xl hover:border-[#BFFF00] transition-colors">
            <CardContent className="pt-5 pb-4">
              <p className="text-sm text-[#121212]/60 mb-1">Needs Signature</p>
              <p className="text-2xl font-bold text-[#121212] font-['Manrope']">{stats.signature}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-amber-200 rounded-2xl hover:border-amber-400 transition-colors">
            <CardContent className="pt-5 pb-4">
              <p className="text-sm text-[#121212]/60 mb-1">Sent</p>
              <p className="text-2xl font-bold text-amber-600 font-['Manrope']">{stats.sent}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-gray-200 rounded-2xl hover:border-gray-400 transition-colors">
            <CardContent className="pt-5 pb-4">
              <p className="text-sm text-[#121212]/60 mb-1">Draft</p>
              <p className="text-2xl font-bold text-[#121212]/50 font-['Manrope']">{stats.draft}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-green-200 rounded-2xl hover:border-green-400 transition-colors">
            <CardContent className="pt-5 pb-4">
              <p className="text-sm text-[#121212]/60 mb-1">Completed</p>
              <p className="text-2xl font-bold text-green-600 font-['Manrope']">{stats.completed}</p>
            </CardContent>
          </Card>
        </div>

        {/* Contracts List or Empty State */}
        {contracts.length === 0 ? (
          <div className="space-y-6">
            <Card className="border-2 border-dashed border-[#BFFF00]/30 bg-[#F7FFE5]/30 rounded-2xl">
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#BFFF00]/30 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-[#BFFF00]">
                    <FileSignature className="w-8 h-8 text-[#0ea5e9]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Create your first contract</h3>
                  <p className="text-gray-500 mb-6">Choose from templates or start from scratch</p>
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" onClick={() => setShowTemplateModal(true)}>
                      <LayoutGrid className="w-4 h-4 mr-2" /> Browse Templates
                    </Button>
                    <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-[#2563eb]">
                      <Plus className="w-4 h-4 mr-2" /> Blank Contract
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Template Preview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {CONTRACT_TEMPLATES.slice(0, 3).map((template) => (
                <Card
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className="border border-gray-200 hover:border-[#2563eb] hover:shadow-md transition-all cursor-pointer group"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-[#e0f2fe]">
                        <FileText className="w-5 h-5 text-gray-500 group-hover:text-[#0ea5e9]" />
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#2563eb]" />
                    </div>
                    <h3 className="font-medium text-gray-900 mt-3">{template.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search contracts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button variant="outline" onClick={() => setShowTemplateModal(true)}>
                <LayoutGrid className="w-4 h-4 mr-2" /> Templates
              </Button>
            </div>

            <Card className="border border-gray-200 overflow-hidden">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Contract</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Recipient</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Date</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredContracts.map((contract) => (
                      <tr key={contract.contract_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <span className="font-medium text-gray-900">{contract.title}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-gray-900">{contract.recipient_name || "-"}</p>
                            {contract.recipient_email && (
                              <p className="text-xs text-gray-500">{contract.recipient_email}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            className={
                              contract.status === "signed" || contract.status === "completed" ? "bg-green-100 text-green-700" :
                              contract.status === "sent" ? "bg-blue-100 text-blue-700" :
                              contract.status === "pending_signature" ? "bg-yellow-100 text-yellow-700" :
                              "bg-gray-100 text-gray-700"
                            }
                          >
                            {contract.status?.replace("_", " ")}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-sm">
                          {contract.created_at ? new Date(contract.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setSelectedContract(contract); setShowViewModal(true); }}>
                                <Eye className="w-4 h-4 mr-2" /> View
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadPDF(contract.contract_id)} data-testid={`download-pdf-${contract.contract_id}`}>
                                <Download className="w-4 h-4 mr-2" /> Download PDF
                              </DropdownMenuItem>
                              {contract.status !== "signed" && contract.status !== "completed" && (
                                <DropdownMenuItem onClick={() => { setSelectedContract(contract); setShowSignModal(true); }}>
                                  <PenLine className="w-4 h-4 mr-2" /> Sign
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleDeleteContract(contract.contract_id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Template Selection Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Choose a Template</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {CONTRACT_TEMPLATES.map((template) => (
              <Card
                key={template.id}
                onClick={() => handleSelectTemplate(template)}
                className="border border-gray-200 hover:border-[#2563eb] hover:shadow-md transition-all cursor-pointer group"
              >
                <CardContent className="p-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-[#e0f2fe] mb-3">
                    <FileText className="w-5 h-5 text-gray-500 group-hover:text-[#0ea5e9]" />
                  </div>
                  <h3 className="font-medium text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowTemplateModal(false); setShowCreateModal(true); }}>
              Start Blank
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Contract Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate ? `New ${selectedTemplate.name}` : "Create Contract"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateContract} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Contract Title *</Label>
              <Input
                id="title"
                value={contractForm.title}
                onChange={(e) => setContractForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Service Agreement - Acme Inc."
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="recipient_name">Recipient Name</Label>
                <Input
                  id="recipient_name"
                  value={contractForm.recipient_name}
                  onChange={(e) => setContractForm(prev => ({ ...prev, recipient_name: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipient_email">Recipient Email</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={contractForm.recipient_email}
                  onChange={(e) => setContractForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                  placeholder="john@example.com"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Contract Content *</Label>
              <Textarea
                id="content"
                value={contractForm.content}
                onChange={(e) => setContractForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Enter the contract terms and conditions..."
                rows={15}
                className="font-mono text-sm"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="bg-[#2563eb]">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Contract
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Contract Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedContract?.title}</DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Badge className={
                  selectedContract.status === "signed" ? "bg-green-100 text-green-700" :
                  selectedContract.status === "sent" ? "bg-blue-100 text-blue-700" :
                  "bg-gray-100 text-gray-700"
                }>
                  {selectedContract.status?.replace("_", " ")}
                </Badge>
                {selectedContract.recipient_name && (
                  <span className="text-sm text-gray-500">To: {selectedContract.recipient_name}</span>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm font-mono">{selectedContract.content}</pre>
              </div>
              {selectedContract.signature_data && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Signature:</p>
                  <img src={selectedContract.signature_data} alt="Signature" className="max-h-24 border rounded" />
                </div>
              )}
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedContract.contract_id)} className="rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                {selectedContract.status !== "signed" && (
                  <Button onClick={() => { setShowViewModal(false); setShowSignModal(true); }} className="bg-[#2563eb]">
                    <PenLine className="w-4 h-4 mr-2" /> Sign Contract
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sign Contract Modal */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Sign Contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              By signing below, you agree to the terms of "{selectedContract?.title}"
            </p>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: "w-full h-40 bg-white",
                  style: { width: "100%", height: "160px" }
                }}
                backgroundColor="white"
              />
            </div>
            <Button variant="outline" size="sm" onClick={clearSignature}>
              Clear Signature
            </Button>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSignModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSignContract} disabled={signing} className="bg-[#10b981] hover:bg-[#059669]">
                {signing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Sign & Complete
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Contract Generation Modal */}
      <Dialog open={showAIModal} onOpenChange={setShowAIModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-500" />
              Generate Contract with AI
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateAIContract} className="space-y-4">
            <div className="space-y-2">
              <Label>Contract Type *</Label>
              <Select value={aiForm.contract_type} onValueChange={(v) => setAiForm(prev => ({ ...prev, contract_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service Agreement</SelectItem>
                  <SelectItem value="nda">Non-Disclosure Agreement (NDA)</SelectItem>
                  <SelectItem value="consulting">Consulting Agreement</SelectItem>
                  <SelectItem value="contractor">Independent Contractor Agreement</SelectItem>
                  <SelectItem value="employment">Employment Agreement</SelectItem>
                  <SelectItem value="partnership">Partnership Agreement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Your Name/Company *</Label>
                <Input
                  value={aiForm.party_a_name}
                  onChange={(e) => setAiForm(prev => ({ ...prev, party_a_name: e.target.value }))}
                  placeholder="Your Company LLC"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Other Party Name *</Label>
                <Input
                  value={aiForm.party_b_name}
                  onChange={(e) => setAiForm(prev => ({ ...prev, party_b_name: e.target.value }))}
                  placeholder="Client Name"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Scope/Description *</Label>
              <Textarea
                value={aiForm.description}
                onChange={(e) => setAiForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the services, work, or purpose of this contract..."
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Key Terms (Optional)</Label>
              <Textarea
                value={aiForm.key_terms}
                onChange={(e) => setAiForm(prev => ({ ...prev, key_terms: e.target.value }))}
                placeholder="Any specific terms, conditions, or requirements..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration</Label>
                <Input
                  value={aiForm.duration}
                  onChange={(e) => setAiForm(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="e.g., 12 months"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Input
                  value={aiForm.payment_terms}
                  onChange={(e) => setAiForm(prev => ({ ...prev, payment_terms: e.target.value }))}
                  placeholder="e.g., $5000/month"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAIModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={generatingAI} className="bg-gradient-to-r from-purple-500 to-indigo-500">
                {generatingAI ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate Contract
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
